const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

import React, { useState, useEffect } from 'react';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import emblaCarouselSchema from './schema';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';

const getHref = (link) => {
  const item = Array.isArray(link) ? link[0] : link;
  if (!item || typeof item !== 'object') return '#';
  if (item['@type'] === 'Link' && item.remoteUrl) {
    return item.remoteUrl;
  }
  const url = item['@id'] || '';
  const isFile = item['@type'] === 'File';
  return url ? (isFile ? `${url}/@@download/file` : url) : '#';
};

// Utility to derive a friendly class from the color value & label
const colorToClass = (value, label) => {
  if (!value) return '';
  const varMatch = /^var\(--([^)]+)\)/.exec(value);
  if (varMatch) return `color-var-${varMatch[1].toLowerCase()}`;
  if (value.startsWith('#')) return `color-${value.slice(1).toLowerCase()}`;
  if (label) return `color-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return '';
};

const Edit = (props) => {
  const { data, block, onChangeBlock, selected } = props;
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.search?.subrequests?.[block]);

  // ----- listing helpers (same as View) -----
  const getImageUrl = (item) => {
    if (item.image_scales && item.image_field) {
      return `${item['@id']}/@@images/${item.image_field}`;
    }
    if (item.preview_image) return `${item.preview_image['@id']}/@@images/image`;
    if (item.lead_image) return `${item.lead_image['@id']}/@@images/image`;
    if (item.image) {
      const image = Array.isArray(item.image) ? item.image[0] : item.image;
      return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
    }
    return '';
  };

  const getListingImageUrl = (item) => {
    if (item['@type'] === 'Image') return `${item['@id']}/@@images/image`;
    return getImageUrl(item);
  };

  const formatEffectiveDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const convertItemToSlide = (item) => ({
    heading: item.title || '',
    content: item.description || '',
    image: null,
    link: item,
    buttonText: data.listingButtonText || 'Read more',
    effectiveDate: item.effective || null,
    isFromListing: true,
  });

  const getSlides = () => {
    let slides = [];
    if (data.useListing && searchResults?.items && searchResults.items.length > 0) {
      slides = searchResults.items.map(convertItemToSlide);
    }
    if (!data.useListing || (data.useListing && data.appendManualSlides)) {
      const manualSlides = (data.slides || []).map((slide) => ({
        ...slide,
        isFromListing: false,
      }));
      slides = slides.concat(manualSlides);
    }
    return slides;
  };

  const slides = getSlides();

  // ----- embla config (mirror View) -----
  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data.minSlidesOnMobile, 10) || 1;
  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(() => {
    const isMobileInit = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    return isMobileInit ? Math.max(1, minSlidesOnMobile) : slidesToShow;
  });

  useEffect(() => {
    const calculateSlidesToShow = () => {
      const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
      setEffectiveSlidesToShow(isMobile ? Math.max(1, minSlidesOnMobile) : slidesToShow);
    };
    calculateSlidesToShow();
    window.addEventListener('resize', calculateSlidesToShow);
    return () => window.removeEventListener('resize', calculateSlidesToShow);
  }, [slidesToShow, minSlidesOnMobile]);

  // fetch listing content
  useEffect(() => {
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: ['title', 'description', 'image_field', 'image_scales', 'effective', 'remoteUrl'],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          const transformedQuery = {};
          data.query.query.forEach((criterion) => {
            if (criterion.i === 'portal_type') transformedQuery.portal_type = criterion.v;
            else if (criterion.i === 'path') transformedQuery.path = criterion.v;
            else if (criterion.i === 'Subject') transformedQuery.Subject = criterion.v;
            else if (criterion.i === 'review_state') transformedQuery.review_state = criterion.v;
          });
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order) transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit) transformedQuery.b_size = parseInt(data.query.limit, 10);
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit) searchOptions.b_size = parseInt(data.query.limit, 10);
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = baseOptions;
      } else {
        searchOptions = { ...baseOptions, query: data.query };
      }

      dispatch(searchContent(searchTerm, searchOptions, block));
    }
  }, [data.useListing, data.query, dispatch, block]);

  const carouselType = data.carouselType || 'default';
  const configOverrides = {
    default: { align: 'start', loop: data.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };
  const presetConfig = getPresetConfig(data.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };

  const plugins = data.autoplay ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 4000, stopOnInteraction: false })] : [];
  const [emblaRef, embla] = useEmblaCarousel(
    {
      ...config,
      align: 'start',
      loop: data.loop ?? true,
      slidesToScroll: effectiveSlidesToShow,
    },
    plugins,
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!embla) return;
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', () => setSelectedIndex(embla.selectedScrollSnap()));
  }, [embla]);

  const scrollPrev = () => embla && embla.scrollPrev();
  const scrollNext = () => embla && embla.scrollNext();
  const scrollTo = (index) => embla && embla.scrollTo(index);

  // ----- mode & classes (mirror View) -----
  const _displayMode = data.displayMode || data.mode || 'full';
  const isImageOnlyMode = _displayMode === 'image-only';
  const isImageTopMode = _displayMode === 'image-top';
  const isLogoMarquee = _displayMode === 'logo-marquee';
  const variantClass = `type-${_displayMode}`;

  const heightClass = data.equalHeight ? 'equal-height' : '';
  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const showArrows = data.hideArrows !== true && slides.length > 1 && !isLogoMarquee;
  const showDots = data.hideDots !== true && slides.length > 0 && !isLogoMarquee;
  const pageCount = Math.ceil(slides.length / effectiveSlidesToShow);

  const colorClass = colorToClass(data.backgroundColor);
  const toneClass = data.textTone === 'light' ? 'tone-light' : 'tone-dark';

  // ----- schema + handlers -----
  const schema = emblaCarouselSchema({
    ...props,
    formData: data,
  });

  const handleFieldChange = (id, value) => {
    if (id === 'slides') {
      const fixed = Array.isArray(value)
        ? value.map((slide) => ({
            ...slide,
            link: Array.isArray(slide.link) ? slide.link[0] : slide.link,
          }))
        : [];
      onChangeBlock(block, { ...data, slides: fixed });
    } else {
      onChangeBlock(block, { ...data, [id]: value });
    }
  };

  const formData = {
    ...data,
    slides: Array.isArray(data.slides)
      ? data.slides.map((slide) => ({
          ...slide,
          link: Array.isArray(slide.link) ? slide.link : slide.link ? [slide.link] : [],
        }))
      : [],
  };

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons || _displayMode === 'image-only') return null;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    return (
      <a
        href={href}
        className="carousel-button"
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;
    const href = getHref(slide.link);
    if (!href || href === '#') return;
    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  // Helper: render image URL for a slide (manual or from listing)
  const imageFor = (slide) => {
    return slide.isFromListing
      ? getListingImageUrl(slide.link)
      : (() => {
          const image = Array.isArray(slide.image) ? slide.image[0] : slide.image;
          return image?.['@id'] ? `${image['@id']}/@@images/image` : image?.download || '';
        })();
  };

  // ===== Render =====
  return (
    <>
      <div
        className={`embla ${heightClass} ${data.isFullWidth ? 'full-width' : ''} align-${data.alignment || 'left'} ${modeClass} ${variantClass} ${toneClass} ${colorClass} ${data.outerClassName || ''}`}
        style={{ backgroundColor: data.backgroundColor || 'transparent' }}
      >
        <div className="embla__inner">
          {data.title ? <h2 className="embla__title">{data.title}</h2> : null}

          {/* Logo marquee preview */}
          {isLogoMarquee ? (
            <div
              className={`logo-marquee ${data.pauseOnHover ? 'pause-on-hover' : ''}`}
              style={{
                '--marquee-duration': `${parseInt(data.marqueeSpeed, 10) || 20}s`,
                '--logo-height': `${parseInt(data.logoHeight, 10) || 48}px`,
                '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
                '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
              }}
            >
              <div className="logo-marquee__track">
                {[...slides, ...slides].map((slide, i) => {
                  const src = imageFor(slide);
                  if (!src) return null;
                  const alt = slide.heading || 'Logo';
                  return (
                    <div className="logo-marquee__item" key={`m-${i}`}>
                      <img className="logo-marquee__img" src={src} alt={alt} />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {showArrows && (
                <>
                  <button className="embla__prev" onClick={scrollPrev}>‹</button>
                  <button className="embla__next" onClick={scrollNext}>›</button>
                </>
              )}

              <div className="embla__viewport" ref={emblaRef}>
                <div className="embla__container">
                  {slides.map((slide, index) => {
                    const imageUrl = imageFor(slide);
                    const href = getHref(slide.link);
                    const isClickable = data.clickableSlides && href && href !== '#';

                    return (
                      <div className="embla__slide" key={index} style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}>
                        <div
                          className={`carousel-slide-inner ${isClickable ? 'clickable' : ''} ${isImageOnlyMode ? 'image-only' : ''} ${isImageTopMode ? 'image-top' : ''} ${slide.isFromListing ? 'listing-slide' : 'manual-slide'}`}
                          style={{
                            backgroundImage: !isImageOnlyMode && !isImageTopMode && imageUrl ? `url(${imageUrl})` : 'none',
                            cursor: isClickable ? 'pointer' : 'default',
                          }}
                          onClick={() => handleSlideClick(slide)}
                        >
                          {isImageOnlyMode ? (
                            imageUrl ? (
                              <img src={imageUrl} alt={slide.heading || 'Slide image'} />
                            ) : (
                              <div className="image-placeholder">No image</div>
                            )
                          ) : isImageTopMode ? (
                            <div className="card card-image-top">
                              {imageUrl ? (
                                <div className="card-media">
                                  <img src={imageUrl} alt={slide.heading || 'Slide image'} />
                                </div>
                              ) : null}
                              <div className="card-content">
                                <h3>{slide.heading}</h3>
                                {!data.hideDescription && <p>{slide.content}</p>}
                                {data.showEffectiveDate && slide.effectiveDate && (
                                  <p className="effective-date">{formatEffectiveDate(slide.effectiveDate)}</p>
                                )}
                                {renderLink(slide)}
                              </div>
                            </div>
                          ) : (
                            <>
                              <h3>{slide.heading}</h3>
                              {!data.hideDescription && <p>{slide.content}</p>}
                              {data.showEffectiveDate && slide.effectiveDate && (
                                <p className="effective-date">{formatEffectiveDate(slide.effectiveDate)}</p>
                              )}
                              {renderLink(slide)}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {showDots && pageCount > 1 && (
                <div className="embla__nav">
                  {Array.from({ length: pageCount }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => scrollTo(i)}
                      className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selected && (
        <SidebarPortal selected={selected}>
          <BlockDataForm
            key={`listing-${data.useListing ? 'on' : 'off'}-append-${data.appendManualSlides ? 'on' : 'off'}`}
            schema={schema}
            title="Embla Carousel"
            onChangeField={handleFieldChange}
            onChangeBlock={onChangeBlock}
            block={block}
            formData={formData}
          />
        </SidebarPortal>
      )}
    </>
  );
};

export default Edit;